import { TFunction } from 'i18next';
import { appConfig } from '../../../config';
import audiAvant from '../../../resources/assets/icons/audi/avant.svg';
import audiCabriolet from '../../../resources/assets/icons/audi/cabriolet.svg';
import audiCoupe from '../../../resources/assets/icons/audi/coupe.svg';
import audiSaloon from '../../../resources/assets/icons/audi/saloon.svg';
import audiSportback from '../../../resources/assets/icons/audi/sportback.svg';
import audiSuv from '../../../resources/assets/icons/audi/suv.svg';
import seatEstate from '../../../resources/assets/icons/seat/estate.svg';
import seatHatchback from '../../../resources/assets/icons/seat/hatchback.svg';
import seatMpv from '../../../resources/assets/icons/seat/mpv.svg';
import seatSuv from '../../../resources/assets/icons/seat/suv.svg';
import skodaEstate from '../../../resources/assets/icons/skoda/estate.svg';
import skodaHatchback from '../../../resources/assets/icons/skoda/hatchback.svg';
import skodaSaloon from '../../../resources/assets/icons/skoda/saloon.svg';
import skodaSuv from '../../../resources/assets/icons/skoda/suv.svg';
import vwMPV from '../../../resources/assets/icons/vw/MPV.svg';
import vwSuv from '../../../resources/assets/icons/vw/SUV.svg';
import vwEstate from '../../../resources/assets/icons/vw/estate.svg';
import vwHatchback from '../../../resources/assets/icons/vw/hatchback.svg';
import vwSaloon from '../../../resources/assets/icons/vw/saloon.svg';
import { LocalBrands } from '../../../shared/types';
import { QuestionItem, SelectableTileCheckboxButtonGroupQuestionItem } from '../types/QuestionItem';
import { Icons } from './icon-helpers';
import { imgSwitch } from './img-helpers';

import { Children } from 'react';
import { FinancePlan } from '../../../custom-types.d';
import {
  FuelType,
  TransmissionType,
  VehicleConditionType,
  VehicleModelBodyType
} from '@bus/models';

export const isAvailableIn = (brands: LocalBrands[]) => brands.includes(appConfig.brandName);

type ModelFuelType =
  | FuelType.V2_GASOLINE
  | FuelType.V2_DIESEL
  | FuelType.V2_HYBRID
  | FuelType.V2_ELECTRICITY;

export type VehicleModelClassification =
  | 'KOMPAKTWAGEN'
  | 'LIMOUSINE'
  | 'SUV'
  | 'COUPE_CABRIOLET'
  | 'SPORTWAGEN'
  | 'NUTZFAHRZEUGE';

type VehicleModelClassificationValue = VehicleModelClassification | VehicleModelClassification[];

const VEHICLE_MODEL_CLASSIFICATION_ORDER: VehicleModelClassification[] = [
  'KOMPAKTWAGEN',
  'LIMOUSINE',
  'SUV',
  'COUPE_CABRIOLET',
  'SPORTWAGEN',
  'NUTZFAHRZEUGE'
];

const GASOLINE: ModelFuelType[] = [FuelType.V2_GASOLINE];
const DIESEL: ModelFuelType[] = [FuelType.V2_DIESEL];
const HYBRID: ModelFuelType[] = [FuelType.V2_HYBRID];
const ELECTRIC: ModelFuelType[] = [FuelType.V2_ELECTRICITY];

const GASOLINE_DIESEL: ModelFuelType[] = [FuelType.V2_GASOLINE, FuelType.V2_DIESEL];

const GASOLINE_HYBRID: ModelFuelType[] = [FuelType.V2_GASOLINE, FuelType.V2_HYBRID];

const DIESEL_HYBRID: ModelFuelType[] = [FuelType.V2_DIESEL, FuelType.V2_HYBRID];

const GASOLINE_DIESEL_HYBRID: ModelFuelType[] = [
  FuelType.V2_GASOLINE,
  FuelType.V2_DIESEL,
  FuelType.V2_HYBRID
];

const DIESEL_HYBRID_ELECTRIC: ModelFuelType[] = [
  FuelType.V2_DIESEL,
  FuelType.V2_HYBRID,
  FuelType.V2_ELECTRICITY
];

const GASOLINE_DIESEL_HYBRID_ELECTRIC: ModelFuelType[] = [
  FuelType.V2_GASOLINE,
  FuelType.V2_DIESEL,
  FuelType.V2_HYBRID,
  FuelType.V2_ELECTRICITY
];

export function carTypeList(t: TFunction): QuestionItem<string>[] {
  return [
    {
      displayName: t('match-maker:question-pages.items.bodystyle.options.HATCHB'),
      value: VehicleModelBodyType.HATCHB,
      description: t('match-maker:question-pages.items.bodystyle.options.HATCHB-example'),
      img: imgSwitch(audiSportback, vwHatchback, skodaHatchback, seatHatchback),
      icon: '',
      trackingName: 'checkboxHatchback',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.bodystyle.options.SEDAN'),
      value: VehicleModelBodyType.SEDAN,
      description: t('match-maker:question-pages.items.bodystyle.options.SEDAN-example'),
      img: imgSwitch(audiSaloon, vwSaloon, skodaSaloon),
      icon: '',
      trackingName: 'checkboxSaloon',
      availableIn: isAvailableIn(['audi'])
    },
    {
      displayName: t('match-maker:question-pages.items.bodystyle.options.COMBI'),
      value: VehicleModelBodyType.COMBI,
      description: t('match-maker:question-pages.items.bodystyle.options.COMBI-example'),
      img: imgSwitch(audiAvant, vwEstate, skodaEstate, seatEstate),
      icon: '',
      trackingName: 'checkboxEstate',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.bodystyle.options.COUPE'),
      value: VehicleModelBodyType.COUPE,
      description: t('match-maker:question-pages.items.bodystyle.options.COUPE-example'),
      img: imgSwitch(audiCoupe),
      icon: '',
      trackingName: 'checkboxCOUPE',
      availableIn: isAvailableIn(['audi'])
    },
    {
      displayName: t('match-maker:question-pages.items.bodystyle.options.SUV'),
      value: VehicleModelBodyType.SUV,
      description: t('match-maker:question-pages.items.bodystyle.options.SUV-example'),
      img: imgSwitch(audiSuv, vwSuv, skodaSuv, seatSuv),
      icon: '',
      trackingName: 'checkboxSUV',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.bodystyle.options.CABRIO'),
      value: VehicleModelBodyType.CABRIO,
      description: t('match-maker:question-pages.items.bodystyle.options.CABRIO-example'),
      img: imgSwitch(audiCabriolet, vwMPV, '', seatMpv),
      icon: '',
      trackingName: 'checkboxMPV',
      availableIn: true
    }
  ];
}

const defineModel = (
  name: string,
  availableFuelTypes: ModelFuelType[] = [],
  vehicleClassification: VehicleModelClassificationValue,
  icon?: string,
  value?: string | number
): QuestionItem<string> => {
  value = String(value ? value : name);

  return {
    displayName: name,
    availableIn: true,
    availableFuelTypes,
    vehicleClassification,
    value,
    icon: icon || '',
    trackingName: `checkbox${value}`
  } as QuestionItem<string>;
};

export const carModelList = ((): Record<LocalBrands, QuestionItem<string>[]> => {
  return {
    audi: [
      defineModel('A1', GASOLINE, 'KOMPAKTWAGEN'),
      defineModel('A1 Sportback', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('A3', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('A3 Sportback', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('A3 Limousine', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A4 Avant', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A4 Limousine', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A5 Avant', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A5 Limousine', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A6 Limousine', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A6 Avant', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A7 Sportback', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A8', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('A5 Sportback', HYBRID, 'LIMOUSINE'),
      defineModel('A6', HYBRID, 'LIMOUSINE'),
      defineModel('A6 Avant e-tron', ELECTRIC, 'LIMOUSINE'),
      defineModel('A6 Sportback e-tron', ELECTRIC, 'LIMOUSINE'),

      defineModel('Q2', GASOLINE_DIESEL, 'SUV'),
      defineModel('Q3', GASOLINE_DIESEL, 'SUV'),
      defineModel('Q3 Sportback', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Q5', GASOLINE, 'SUV'),
      defineModel('Q5 Sportback', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Q7', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Q8', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Q3 SUV', DIESEL_HYBRID, 'SUV'),
      defineModel('Q5 SUV', DIESEL_HYBRID, 'SUV'),
      defineModel('Q4 e-tron', ELECTRIC, 'SUV'),
      defineModel('Q4 Sportback e-tron', ELECTRIC, 'SUV'),
      defineModel('Q6 e-tron', ELECTRIC, 'SUV'),
      defineModel('Q6 Sportback e-tron', ELECTRIC, 'SUV'),
      defineModel('Q8 e-tron', ELECTRIC, 'SUV'),
      defineModel('Q8 Sportback e-tron', ELECTRIC, 'SUV'),

      defineModel('A5 Coupé', GASOLINE_DIESEL_HYBRID, 'COUPE_CABRIOLET'),

      defineModel('TT', GASOLINE_DIESEL, 'SPORTWAGEN'),
      defineModel('R8', GASOLINE, 'SPORTWAGEN'),
      defineModel('e-tron GT', ELECTRIC, 'SPORTWAGEN')
    ],

    seat: [
      defineModel('SEAT Ibiza', GASOLINE, 'KOMPAKTWAGEN'),
      defineModel('SEAT Leon', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('SEAT Leon Sportstourer', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('SEAT Arona', GASOLINE, 'SUV')
    ],

    cupra: [
      defineModel('CUPRA Leon', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('CUPRA Leon Sportstourer', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('CUPRA Raval', ELECTRIC, 'KOMPAKTWAGEN'),
      defineModel('CUPRA Born', ELECTRIC, 'KOMPAKTWAGEN'),
      defineModel('CUPRA Formentor', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('CUPRA Terramar', GASOLINE_HYBRID, 'SUV'),
      defineModel('CUPRA Tavascan', ELECTRIC, 'SUV')
    ],

    vw: [
      defineModel('Der UP', GASOLINE, 'KOMPAKTWAGEN'),
      defineModel('Der Polo', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Golf', GASOLINE_DIESEL_HYBRID_ELECTRIC, 'KOMPAKTWAGEN'),
      defineModel('Der Golf Plus', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Golf Sportsvan', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Golf Variant', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Beetle', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der T-Cross', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Taigo', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der e-Up', ELECTRIC, 'KOMPAKTWAGEN'),
      defineModel('Der ID. Polo', ELECTRIC, 'KOMPAKTWAGEN'),
      defineModel('Der ID.3', ELECTRIC, 'KOMPAKTWAGEN'),
      defineModel('Der ID.3 Neo', ELECTRIC, 'KOMPAKTWAGEN'),

      defineModel('Der Jetta', GASOLINE, 'LIMOUSINE'),
      defineModel('Der Passat', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('Der Passat Variant', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('Der Arteon', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('Der Arteon Shooting Brake', GASOLINE_DIESEL_HYBRID, 'LIMOUSINE'),
      defineModel('Der ID.7', ELECTRIC, 'LIMOUSINE'),
      defineModel('Der ID.7 Tourer', ELECTRIC, 'LIMOUSINE'),

      defineModel('Der T-Roc', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Der Tayron', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Der Tiguan', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Der Tiguan Allspace', GASOLINE_DIESEL, 'SUV'),
      defineModel('Der Touareg', GASOLINE_DIESEL_HYBRID, 'SUV'),
      defineModel('Der ID.4', ELECTRIC, 'SUV'),

      defineModel('Der ID.5', ELECTRIC, ['SUV']),

      defineModel('Der Eos', GASOLINE_DIESEL, 'COUPE_CABRIOLET'),
      defineModel('Der Scirocco', GASOLINE_DIESEL, 'COUPE_CABRIOLET'),
      defineModel('Der Golf Cabriolet', GASOLINE_DIESEL, 'COUPE_CABRIOLET'),
      defineModel('Der Beetle Cabriolet', GASOLINE_DIESEL, 'COUPE_CABRIOLET'),
      defineModel('Der T-Roc Cabriolet', GASOLINE_DIESEL, 'COUPE_CABRIOLET'),

      defineModel('Der Caddy', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Caddy Maxi', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Caddy Kombi', GASOLINE_DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Caddy Cargo', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Caddy California', GASOLINE_DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Amarok', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Multivan', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der California', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Caravelle', DIESEL_HYBRID_ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter Kombi', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter Pritsche', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter Kasten', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Grand California', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Crafter', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der ID. Buzz', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der ID. Buzz Cargo', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Caravelle', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter Kombi', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter Pritsche', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter Kasten', ELECTRIC, 'NUTZFAHRZEUGE')
    ],

    vwn: [
      defineModel('Der Caddy', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),
      defineModel('Der Caddy California', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Caddy Kombi', GASOLINE_DIESEL, 'KOMPAKTWAGEN'),
      defineModel('Der Caddy Maxi', GASOLINE_DIESEL_HYBRID, 'KOMPAKTWAGEN'),

      defineModel('Der Multivan', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Caddy Cargo', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der California', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter', GASOLINE_DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Amarok', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Caravelle', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter Kombi', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter Pritsche', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Transporter Kasten', DIESEL_HYBRID, 'NUTZFAHRZEUGE'),
      defineModel('Der Grand California', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der Crafter', DIESEL, 'NUTZFAHRZEUGE'),
      defineModel('Der ID. Buzz', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der ID. Buzz Cargo', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Caravelle', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter Kombi', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter Pritsche', ELECTRIC, 'NUTZFAHRZEUGE'),
      defineModel('Der e-Transporter Kasten', ELECTRIC, 'NUTZFAHRZEUGE')
    ]
  };
})();

export const filterModelsByFuelTypes = (
  models: QuestionItem<string>[],
  selectedFuelTypes: FuelType[] = []
): QuestionItem<string>[] => {
  if (selectedFuelTypes.length === 0) return [];

  return models.filter((model) =>
    model.availableFuelTypes?.some((availableFuelType) =>
      selectedFuelTypes.includes(availableFuelType as FuelType)
    )
  );
};

export const getCarModelsForBrand = (brand: LocalBrands): QuestionItem<string>[] => {
  if (brand === 'seat') {
    return [...carModelList.seat, ...carModelList.cupra];
  }

  return carModelList[brand];
};

export const groupModelsByClassification = (
  models: QuestionItem<string>[]
): Array<{
  classification: VehicleModelClassification;
  items: QuestionItem<string>[];
}> => {
  return VEHICLE_MODEL_CLASSIFICATION_ORDER.reduce<
    Array<{
      classification: VehicleModelClassification;
      items: QuestionItem<string>[];
    }>
  >((result, classification) => {
    const items = models.filter((model) => {
      const modelClassification = model.vehicleClassification as VehicleModelClassificationValue;

      return Array.isArray(modelClassification)
        ? modelClassification.includes(classification)
        : modelClassification === classification;
    });

    if (items.length > 0) {
      result.push({
        classification,
        items
      });
    }

    return result;
  }, []);
};

export function transmissionList(t: TFunction): QuestionItem<TransmissionType>[] {
  return [
    {
      displayName: t('match-maker:question-pages.items.gears.options.one'),
      value: TransmissionType.MANUAL,
      icon: Icons.TransmissionManual,
      trackingName: 'checkboxManual',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.gears.options.two'),
      value: TransmissionType.AUTOMATIC,
      icon: Icons.TransmissionAutomatic,
      trackingName: 'checkboxAutomatic',
      availableIn: true
    }
  ];
}

export function fuelList(t: TFunction): QuestionItem<FuelType>[] {
  return [
    {
      displayName: t('match-maker:question-pages.items.fuel.options.GASOLINE'),
      value: FuelType.V2_GASOLINE,
      icon: Icons.FuelPetrol,
      trackingName: 'checkboxPetrol',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.fuel.options.DIESEL'),
      value: FuelType.V2_DIESEL,
      icon: Icons.FuelDiesel,
      trackingName: 'checkboxDiesel',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.fuel.options.CNG'),
      value: FuelType.CNG,
      icon: Icons.FuelNaturalGas,
      trackingName: 'checkboxNaturalGas',
      availableIn: false
    },
    {
      displayName: t('match-maker:question-pages.items.fuel.options.V2_HYBRID'),
      value: FuelType.V2_HYBRID,
      icon: Icons.FuelHybrid,
      trackingName: 'checkboxHybrid',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.fuel.options.ELECTRICITY'),
      value: FuelType.V2_ELECTRICITY,
      icon: Icons.FuelElectric,
      trackingName: 'checkboxElectric',
      availableIn: true
    }
  ];
}

export function financePlanList(
  t: TFunction
): SelectableTileCheckboxButtonGroupQuestionItem<FinancePlan>[] {
  return [
    {
      displayName: t('match-maker:question-pages.items.finance-plan.options.one'),
      summaryDisplayName: t('match-maker:question-pages.items.finance-plan.options.summaryOne'),
      description: (
        <ul className="c-ul u-mt-xsmall u-text-left u-font-size-fs-1 u-ml-xxsmall">
          <li>
            {t('match-maker:question-pages.items.finance-plan.options.one-description.item-one')}
          </li>
          <li>
            {t('match-maker:question-pages.items.finance-plan.options.one-description.item-two')}
          </li>
        </ul>
      ),
      value: FinancePlan.Leasing,
      icon: 'semantic-car-return',
      trackingName: 'checkboxLeasing',
      availableIn: true,
      hasVideo: appConfig.brandName === 'audi'
    },
    {
      displayName: t('match-maker:question-pages.items.finance-plan.options.two'),
      summaryDisplayName: t('match-maker:question-pages.items.finance-plan.options.summaryTwo'),
      description: (
        <>
          <ul className="c-ul u-mt-xsmall u-text-left u-font-size-fs-1 u-ml-xxsmall">
            <li>
              {t('match-maker:question-pages.items.finance-plan.options.two-description.item-one')}
            </li>
            <li>
              {t('match-maker:question-pages.items.finance-plan.options.two-description.item-two')}
            </li>
          </ul>
          <p className="u-mt-xsmall u-text-left u-font-size-fs-1 u-ml-xxsmall">
            <b>
              {t(
                'match-maker:question-pages.items.finance-plan.options.two-description.item-three'
              )}
            </b>{' '}
            {t('match-maker:question-pages.items.finance-plan.options.two-description.item-four')}
          </p>
        </>
      ),
      value: FinancePlan.Credit,
      icon: 'semantic-benefit-finance',
      trackingName: 'checkboxCredit',
      availableIn: true,
      hasVideo: appConfig.brandName === 'audi'
    },
    {
      displayName: t('match-maker:question-pages.items.finance-plan.options.three'),
      summaryDisplayName: t('match-maker:question-pages.items.finance-plan.options.summaryThree'),
      description: (
        <>
          <ul className="c-ul u-mt-xsmall u-text-left u-font-size-fs-1 u-ml-xxsmall">
            <li>
              {t(
                'match-maker:question-pages.items.finance-plan.options.three-description.item-one'
              )}
            </li>
            <li>
              {t(
                'match-maker:question-pages.items.finance-plan.options.three-description.item-two'
              )}
            </li>
          </ul>
          <p className="u-mt-xsmall u-text-left u-font-size-fs-1 u-ml-xxsmall">
            <b>
              {t(
                'match-maker:question-pages.items.finance-plan.options.three-description.item-three'
              )}
            </b>{' '}
            {t('match-maker:question-pages.items.finance-plan.options.three-description.item-four')}
          </p>
        </>
      ),
      value: FinancePlan.FollowUpFinancing,
      icon: 'semantic-signature-digital',
      trackingName: 'checkboxFinancing',
      availableIn: true,
      hasVideo: appConfig.brandName === 'audi'
    }
  ];
}

export function conditionList(t: TFunction): QuestionItem<string>[] {
  return [
    {
      displayName: t('match-maker:question-pages.items.condition.options.one'),
      value: VehicleConditionType.NEW,
      icon: 'semantic-car-handover',
      trackingName: 'checkboxConditionNew',
      availableIn: true
    },
    {
      displayName: t('match-maker:question-pages.items.condition.options.two'),
      value: VehicleConditionType.USED,
      icon: 'semantic-car',
      trackingName: 'checkboxConditionUsed',
      availableIn: true
    }
  ];
}

function fieldOptions(t: TFunction): {
  [key: string]:
    | QuestionItem<string | number>[]
    | SelectableTileCheckboxButtonGroupQuestionItem<string | number>[];
} {
  return {
    typeOfCar: carTypeList(t),
    modelOfCar: [
      ...carModelList.audi,
      ...carModelList.seat,
      ...carModelList.cupra,
      ...carModelList.vw,
      ...carModelList.vwn
    ],
    typeOfGears: transmissionList(t),
    fuelTypes: fuelList(t),
    condition: conditionList(t),
    financePlan: financePlanList(t)
  };
}

export const getFieldSummaryText = (
  objectContainsField: object,
  fieldName: string,
  t: TFunction
): string => {
  const questionItemList:
    | QuestionItem<string | number>[]
    | SelectableTileCheckboxButtonGroupQuestionItem<string | number>[] = fieldOptions(t)[fieldName];

  const results: string[] = [];

  // @ts-ignore
  objectContainsField[fieldName].forEach((value) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const displayItem = (questionItemList as QuestionItem<any>[]).find(
      (item) => item.value === value
    );

    if (displayItem && displayItem.summaryDisplayName) {
      results.push(displayItem.summaryDisplayName);
    } else if (displayItem) {
      results.push(displayItem.displayName);
    }
  });

  return results.join(', ');
};

/**
 * - Render children for brands specified in props only.
 * - Return `null` otherwise.
 */
export const RenderForBrands: React.FC<Partial<Record<LocalBrands, boolean>>> = ({
  audi,
  seat,
  cupra,
  vw,
  vwn,
  children
}) => {
  const allowedBrands = ((): Array<LocalBrands> => {
    const _brands = [] as Array<LocalBrands>;

    if (audi) _brands.push('audi');
    if (seat) _brands.push('seat');
    if (cupra) _brands.push('cupra');
    if (vw) _brands.push('vw');
    if (vwn) _brands.push('vwn');

    if (!_brands.length) _brands.push('audi');

    return _brands;
  })();

  if (!allowedBrands.includes(appConfig.brandName)) return null;
  if (!children) return null;

  const count = Children.count(children);

  return count > 1 ? <>{children}</> : (children as JSX.Element);
};
